/**
 * Feature Flag System for Safe Rollouts
 *
 * Enables gradual deployment of new features:
 * - Test locally with 100% flag
 * - Deploy to prod with 10% rollout
 * - Monitor metrics (Sentry + analytics)
 * - Roll out to 100% if successful
 *
 * Usage:
 *   if (isFeatureEnabled('new_matching_algorithm')) {
 *     // Use new code
 *   } else {
 *     // Use old code
 *   }
 */

import { prodLogger } from '../utils/productionLogger';

export type FeatureFlag =
  | 'new_matching_algorithm'
  | 'enhanced_error_boundaries'
  | 'timeout_protection'
  | 'sentry_monitoring';

interface FeatureFlagConfig {
  enabled: boolean;
  rolloutPercentage: number; // 0-100
  users?: string[]; // Specific user IDs (for testing)
  environments?: ('development' | 'staging' | 'production')[];
}

/**
 * Feature flag configuration
 * Update these values to control rollouts
 */
const FEATURE_FLAGS: Record<FeatureFlag, FeatureFlagConfig> = {
  // NEW: Deterministic matching algorithm (replaces random fallback)
  new_matching_algorithm: {
    enabled: true, // Set to true to enable
    rolloutPercentage: 100, // Start at 100 for local testing, 10 for prod
    environments: ['development', 'staging'], // Remove 'production' initially
  },

  // NEW: Enhanced error boundaries
  enhanced_error_boundaries: {
    enabled: true,
    rolloutPercentage: 100,
    environments: ['development', 'staging'],
  },

  // NEW: Timeout protection on API calls
  timeout_protection: {
    enabled: true,
    rolloutPercentage: 100,
    environments: ['development', 'staging'],
  },

  // NEW: Sentry error monitoring
  sentry_monitoring: {
    enabled: true,
    rolloutPercentage: 100,
    environments: ['development', 'staging', 'production'],
  },
};

/**
 * Get current environment
 */
function getCurrentEnvironment(): 'development' | 'staging' | 'production' {
  // Detect environment from various sources
  const nodeEnv = import.meta.env.MODE;
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  if (nodeEnv === 'production' || hostname.includes('subvention360.com')) {
    return 'production';
  }
  if (hostname.includes('staging') || hostname.includes('vercel.app')) {
    return 'staging';
  }
  return 'development';
}

/**
 * Generate consistent hash for user ID (for percentage rollout)
 */
function hashUserId(userId: string): number {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash % 100);
}

/**
 * Check if feature is enabled for current user/environment
 *
 * @param flag - Feature flag name
 * @param userId - Optional user ID for percentage rollout
 * @returns boolean - Whether feature is enabled
 */
export function isFeatureEnabled(flag: FeatureFlag, userId?: string): boolean {
  const config = FEATURE_FLAGS[flag];
  const env = getCurrentEnvironment();

  // 1. Check if feature is globally enabled
  if (!config.enabled) {
    return false;
  }

  // 2. Check environment restriction
  if (config.environments && !config.environments.includes(env)) {
    return false;
  }

  // 3. Check specific user allowlist (for testing)
  if (config.users && userId && config.users.includes(userId)) {
    return true;
  }

  // 4. Check percentage rollout (consistent per user)
  if (config.rolloutPercentage >= 100) {
    return true;
  }

  if (config.rolloutPercentage <= 0) {
    return false;
  }

  // If no userId provided, use random assignment (sticky via sessionStorage)
  if (!userId && typeof window !== 'undefined') {
    const sessionKey = `ff_${flag}`;
    const cached = sessionStorage.getItem(sessionKey);
    if (cached !== null) {
      return cached === 'true';
    }

    const randomAssignment = Math.random() * 100 < config.rolloutPercentage;
    sessionStorage.setItem(sessionKey, String(randomAssignment));
    return randomAssignment;
  }

  // Percentage-based rollout (consistent per user)
  if (userId) {
    const userHash = hashUserId(userId);
    return userHash < config.rolloutPercentage;
  }

  return false;
}

/**
 * Get all enabled features for debugging
 */
export function getEnabledFeatures(userId?: string): FeatureFlag[] {
  return (Object.keys(FEATURE_FLAGS) as FeatureFlag[]).filter(flag =>
    isFeatureEnabled(flag, userId)
  );
}

/**
 * Force enable a feature for current session (testing only)
 */
export function forceEnableFeature(flag: FeatureFlag): void {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem(`ff_${flag}`, 'true');
    prodLogger.debug(`✅ Feature "${flag}" force-enabled for this session`);
  }
}

/**
 * Force disable a feature for current session (testing only)
 */
export function forceDisableFeature(flag: FeatureFlag): void {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem(`ff_${flag}`, 'false');
    prodLogger.debug(`❌ Feature "${flag}" force-disabled for this session`);
  }
}

/**
 * Clear feature flag cache
 */
export function clearFeatureFlagCache(): void {
  if (typeof window !== 'undefined') {
    const keys = Object.keys(FEATURE_FLAGS);
    keys.forEach(key => {
      sessionStorage.removeItem(`ff_${key}`);
    });
    prodLogger.debug('🔄 Feature flag cache cleared');
  }
}

/**
 * Debug: Log feature flag status
 */
export function debugFeatureFlags(userId?: string): void {
  const env = getCurrentEnvironment();
  prodLogger.debug('🚩 Feature Flags Debug');
  prodLogger.debug('─────────────────────');
  prodLogger.debug(`Environment: ${env}`);
  prodLogger.debug(`User ID: ${userId || 'Anonymous'}`);
  prodLogger.debug('');

  (Object.keys(FEATURE_FLAGS) as FeatureFlag[]).forEach(flag => {
    const config = FEATURE_FLAGS[flag];
    const enabled = isFeatureEnabled(flag, userId);

    prodLogger.debug(`${enabled ? '✅' : '❌'} ${flag}`);
    prodLogger.debug(`   ├─ Global: ${config.enabled ? 'ON' : 'OFF'}`);
    prodLogger.debug(`   ├─ Rollout: ${config.rolloutPercentage}%`);
    prodLogger.debug(`   └─ Envs: ${config.environments?.join(', ') || 'All'}`);
  });
}

// Make available in browser console for debugging
if (typeof window !== 'undefined') {
  (window as any).__featureFlags = {
    isEnabled: isFeatureEnabled,
    getEnabled: getEnabledFeatures,
    forceEnable: forceEnableFeature,
    forceDisable: forceDisableFeature,
    clear: clearFeatureFlagCache,
    debug: debugFeatureFlags,
  };

  prodLogger.debug(
    '💡 Feature flags available in console: window.__featureFlags'
  );
}
